// IND-024: previsión a 8 semanas con dos líneas, a partir del mismo
// recorrido de saldo de la cobertura (timeline_events de la fila).
//  · Solo OF: stock + compras − necesidad de OF hasta cada día (la de hoy).
//  · Con el ritmo habitual: stock + compras − el mayor entre la necesidad
//    de OF acumulada y ritmo × días laborables acumulados (lo que cubren
//    las OF no se cuenta dos veces). Días laborables: lunes a viernes sin
//    festivos; hoy cuenta.
import type { MaterialStockTimelineEvent } from '../types/industrialCost';

export const FORECAST_DAYS = 56;

export type ForecastPoint = {
  date: string;
  ofBalance: number;
  rhythmBalance: number | null;
};

export type ForecastPurchase = {
  date: string;
  qty: number;
  references: string[];
};

export type MaterialForecast = {
  points: ForecastPoint[];
  purchases: ForecastPurchase[];
  ofShortageDate: string | null;
  rhythmShortageDate: string | null;
};

function toKey(value: Date): string {
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, '0');
  const d = String(value.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function addDaysKey(date: string, offset: number): string {
  const parsed = new Date(`${date.slice(0, 10)}T12:00:00`);
  parsed.setDate(parsed.getDate() + offset);
  return toKey(parsed);
}

export function isWorkday(date: string, holidays: ReadonlySet<string>): boolean {
  const day = new Date(`${date.slice(0, 10)}T12:00:00`).getDay();
  return day !== 0 && day !== 6 && !holidays.has(date.slice(0, 10));
}

export function buildMaterialForecast({
  stock,
  timelineEvents,
  today,
  perWorkday,
  holidays = [],
  days = FORECAST_DAYS,
}: {
  stock: number | null;
  timelineEvents: MaterialStockTimelineEvent[];
  today: string;
  perWorkday: number | null;
  holidays?: string[];
  days?: number;
}): MaterialForecast | null {
  if (stock === null || !Number.isFinite(stock)) return null;
  const start = today.slice(0, 10);
  const holidaySet = new Set(holidays.map((day) => day.slice(0, 10)));
  const rhythm = perWorkday !== null && Number.isFinite(perWorkday) && perWorkday > 0 ? perWorkday : null;

  // Lo vencido o sin fecha ya viene con fecha de hoy en el recorrido.
  const purchaseByDay = new Map<string, ForecastPurchase>();
  const requirementByDay = new Map<string, number>();
  for (const event of timelineEvents) {
    const date = event.effective_date.slice(0, 10) < start ? start : event.effective_date.slice(0, 10);
    if (event.event_type === 'purchase') {
      const current = purchaseByDay.get(date) ?? { date, qty: 0, references: [] };
      current.qty += Math.max(event.quantity_delta, 0);
      if (event.reference) current.references.push(event.reference);
      purchaseByDay.set(date, current);
    } else {
      requirementByDay.set(date, (requirementByDay.get(date) ?? 0) + Math.abs(Math.min(event.quantity_delta, 0)));
    }
  }

  const points: ForecastPoint[] = [];
  let purchasesCum = 0;
  let ofCum = 0;
  let workdaysCum = 0;
  let ofShortageDate: string | null = null;
  let rhythmShortageDate: string | null = null;

  for (let index = 0; index < days; index += 1) {
    const date = addDaysKey(start, index);
    purchasesCum += purchaseByDay.get(date)?.qty ?? 0;
    ofCum += requirementByDay.get(date) ?? 0;
    if (isWorkday(date, holidaySet)) workdaysCum += 1;
    const ofBalance = stock + purchasesCum - ofCum;
    const rhythmBalance = rhythm === null ? null : stock + purchasesCum - Math.max(ofCum, rhythm * workdaysCum);
    if (ofShortageDate === null && ofBalance < 0) ofShortageDate = date;
    if (rhythmShortageDate === null && rhythmBalance !== null && rhythmBalance < 0) rhythmShortageDate = date;
    points.push({ date, ofBalance, rhythmBalance });
  }

  const end = addDaysKey(start, days - 1);
  return {
    points,
    purchases: [...purchaseByDay.values()]
      .filter((item) => item.date <= end)
      .sort((a, b) => a.date.localeCompare(b.date)),
    ofShortageDate,
    rhythmShortageDate,
  };
}

// Días laborables que cubre el stock de hoy al ritmo habitual.
export function coverageWorkdays(stock: number | null, perWorkday: number | null): number | null {
  if (stock === null || perWorkday === null || !Number.isFinite(stock) || perWorkday <= 0) return null;
  return Math.max(stock, 0) / perWorkday;
}
